import { Copy, Eye, EyeClosed, Pencil, Trash } from '@phosphor-icons/react';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { isLimitError } from 'src/api/requests';
import { Button } from 'src/components/Button';
import { CopyableField } from 'src/components/CopyableField';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { Modal } from 'src/components/Modal';
import { SideDropActions } from 'src/components/SideDropActions';
import { Switcher } from 'src/components/Switcher';
import Table from 'src/components/Table';
import { Tooltip } from 'src/components/Tooltip';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Can, can, Subject } from 'src/hooks/usePermissions';
import { useUsersPreferences } from 'src/hooks/useUsersPreferences';
import {
  Environment,
  UpdateEnvironmentDto,
} from 'src/interfaces/environment.interface';
import { obfuscateToken } from 'src/utils/token';

const apiKeyHeaders = [
  { key: 'id', label: 'ID', width: '25%', disableSorting: true },
  {
    key: 'name',
    label: 'Environment Name',
    width: '15%',
    disableSorting: true,
  },
  {
    key: 'deploymentKeyElement',
    label: 'API Key',
    width: '20%',
    disableSorting: true,
  },
  {
    key: 'maxConcurrentCalls',
    label: 'Max Concurrent Calls',
    width: '15%',
    disableSorting: true,
  },
  { key: 'createdAt', label: 'Created At', width: '15%', disableSorting: true },
  { key: 'hidden', label: 'Hidden', width: '5%', disableSorting: true },
  { key: 'actions', label: '', width: '10%', disableSorting: true },
];

interface ApiKeysProps {
  hiddenEnvironmentsIds: string[];
  environmentList: Environment[];
  fetchEnvironments: (page: number, perPage: number) => Promise<void>;
  handleCreateEnvironment: () => void;
}

export const ApiKeys = ({
  hiddenEnvironmentsIds,
  environmentList,
  fetchEnvironments,
  handleCreateEnvironment,
}: ApiKeysProps) => {
  const { patch, remove } = useAuthedRequest();
  const notification = useNotification();

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedApiKey, setSelectedApiKey] = useState<Environment | null>(
    {} as Environment,
  );

  const [providersKeyObfuscated, setProvidersKeyObfuscated] = useState<{
    [key: string]: boolean;
  }>({});
  const [loading, setLoading] = useState(false);
  const [size, setSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [, setApiKeySortConfig] = useState<{
    key: string;
    direction: string;
  } | null>(null);

  const [modalDeleteOpen, setModalDeleteOpen] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState('');

  const { role } = useEnvironment();
  const { upsertUserPreferences } = useUsersPreferences();

  const fetchData = async (page: number, perPage: number) => {
    setLoading(true);

    await fetchEnvironments(page, perPage);

    setTotalItems(environmentList.length);
    setLoading(false);
  };

  useEffect(() => {
    fetchData(currentPage, size);
  }, [currentPage]);

  useEffect(() => {
    setLoading(true);

    for (const item of environmentList) {
      providersKeyObfuscated[item.id] = true;
    }

    setProvidersKeyObfuscated(providersKeyObfuscated);
    setLoading(false);
  }, [environmentList]);

  const toggleProvidersKeyObfuscated = (key: string) => {
    setProvidersKeyObfuscated({
      ...providersKeyObfuscated,
      [key]: !providersKeyObfuscated[key],
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    notification.success('Copied to clipboard');
  };

  const openEditModal = (apiKey: Environment) => {
    setSelectedApiKey(apiKey);
    setModalOpen(true);
  };

  const closeModal = () => {
    setSelectedApiKey({} as Environment);
    setModalOpen(false);
  };

  const submitModal = async () => {
    if (!selectedApiKey) return;

    if (
      selectedApiKey.limits.maxConcurrentCalls === undefined ||
      isNaN(selectedApiKey.limits.maxConcurrentCalls)
    ) {
      notification.error(
        'A numeric value for max concurrent calls is required',
      );
      return;
    }

    setLoading(true);
    try {
      const payload: UpdateEnvironmentDto = {
        name: selectedApiKey.name,
        limits: {
          ...selectedApiKey.limits,
          maxConcurrentCalls: selectedApiKey.limits.maxConcurrentCalls,
        },
        features: selectedApiKey.features,
      };

      try {
        await patch(`/environments/${selectedApiKey.id}`, payload);
      } catch (error) {
        if (!isLimitError(error)) {
          console.error(error);
          notification.error('Failed to update environment');
        }
        return;
      }

      fetchData(currentPage, size);
      closeModal();
      notification.success('Environment updated successfully');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = (id: string) => {
    setModalDeleteOpen(true);
    setSelectedDeleteId(id);
  };

  const deleteApiKey = async () => {
    setLoading(true);

    try {
      await remove(`/environments/${selectedDeleteId}`);

      notification.success('Environment deleted successfully');
      await fetchData(currentPage, size);
      setModalDeleteOpen(false);
    } catch {
      notification.error('Failed to delete environment');
    } finally {
      setLoading(false);
    }
  };

  const toggleHidden = async (id: string) => {
    setLoading(true);

    try {
      const newHiddenEnvironmentsIds = hiddenEnvironmentsIds.includes(id)
        ? hiddenEnvironmentsIds.filter((environmentId) => environmentId !== id)
        : [...hiddenEnvironmentsIds, id];

      await upsertUserPreferences({
        hiddenEnvironmentsIds: newHiddenEnvironmentsIds,
      });

      await fetchData(currentPage, size);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-6">
      <div className="flex justify-between mb-4 items-center">
        <h3>Environments</h3>

        <Can operation="create" subject={Subject.ENVIRONMENTS}>
          <Button disabled={loading} onClick={handleCreateEnvironment}>
            Create Environment
          </Button>
        </Can>
      </div>

      <Table
        headers={apiKeyHeaders}
        rows={environmentList.map((item) => ({
          id: <CopyableField value={item.id} notification={notification} />,
          name: (
            <div
              className="text-bold underline cursor-pointer"
              onClick={() => openEditModal(item)}
            >
              {item.name}
            </div>
          ),
          createdAt: format(new Date(item.createdAt), 'yyyy-MM-dd HH:mm:ss'),
          organization: item.organization.name,
          deploymentKeyElement: (
            <div>
              <span
                className="cursor-pointer"
                onClick={() => toggleProvidersKeyObfuscated(item.id)}
              >
                {providersKeyObfuscated[item.id]
                  ? obfuscateToken(item.deploymentKey)
                  : item.deploymentKey}{' '}
                {providersKeyObfuscated[item.id] ? (
                  <Eye className="inline-block w-5 h-5" />
                ) : (
                  <EyeClosed className="inline-block w-5 h-5" />
                )}
              </span>

              <Tooltip content="Copy to clipboard" position="top">
                <Copy
                  className="cursor-pointer inline-block w-5 h-5"
                  onClick={() => copyToClipboard(item.deploymentKey)}
                />
              </Tooltip>
            </div>
          ),
          hidden: (
            <div className="flex justify-center items-center">
              <Tooltip content="This environment will be hidden only for you in the left sidebar.">
                <Switcher
                  enabled={hiddenEnvironmentsIds.includes(item.id)}
                  setEnabled={() => toggleHidden(item.id)}
                />
              </Tooltip>
            </div>
          ),
          actions: (
            <div className="flex justify-center items-center">
              <SideDropActions
                options={[
                  {
                    label: 'Edit',
                    Icon: Pencil,
                    onClick: () => openEditModal(item),
                    show: can(role, 'update', Subject.ENVIRONMENTS),
                  },
                  {
                    label: 'Delete',
                    Icon: Trash,
                    color: 'error',
                    onClick: () => openDeleteModal(item.id),
                    show: can(role, 'delete', Subject.ENVIRONMENTS),
                  },
                ]}
              />
            </div>
          ),
          maxConcurrentCalls: item.limits.maxConcurrentCalls ?? '—',
        }))}
        totalItems={totalItems}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        loading={loading || Object.keys(providersKeyObfuscated).length === 0}
        setSize={setSize}
        defaultSize={size}
        onSort={setApiKeySortConfig}
      />

      <Modal
        title={`Edit Environment`}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        actionButton={
          <Button
            onClick={() => submitModal()}
            disabled={loading || !selectedApiKey || selectedApiKey.name === ''}
          >
            Save Environment
          </Button>
        }
      >
        <div className="flex justify-end italic text-sm">* Required fields</div>

        <div className="grid grid-cols-1 gap-6">
          <div>
            <Label required htmlFor="environment-name">
              Environment Name
            </Label>

            <Input
              type="text"
              id="environment-name"
              placeholder="Enter environment name"
              onChange={(e) => {
                if (!selectedApiKey) return;

                setSelectedApiKey({
                  ...selectedApiKey,
                  name: e.target.value,
                });
              }}
              value={selectedApiKey?.name ?? ''}
              disabled={loading}
            />
          </div>
          <div>
            <Label required htmlFor="environment-name">
              Max Concurrent Calls
            </Label>

            <Input
              type="number"
              id="max-concurrent-calls"
              min="0"
              placeholder="Enter max concurrent calls"
              onChange={(e) => {
                if (!selectedApiKey) return;

                setSelectedApiKey({
                  ...selectedApiKey,
                  limits: {
                    ...selectedApiKey.limits,
                    maxConcurrentCalls: parseInt(e.target.value),
                  },
                });
              }}
              value={selectedApiKey?.limits?.maxConcurrentCalls ?? ''}
              disabled={loading}
            />
          </div>
          <div className="flex flex-col">
            <div className="flex gap-2">
              <Label htmlFor="enable-experimental-features">
                Enable Experimental Features
              </Label>
              <Tooltip
                zIndex={50}
                content="Enable early-access features for this environment. These may change or be removed at any time."
              >
                <Switcher
                  enabled={
                    selectedApiKey?.features?.enableExperimentalFeatures ||
                    false
                  }
                  setEnabled={() => {
                    if (!selectedApiKey) return;
                    setSelectedApiKey({
                      ...selectedApiKey,
                      features: {
                        ...selectedApiKey?.features,
                        enableExperimentalFeatures:
                          !selectedApiKey?.features?.enableExperimentalFeatures,
                      },
                    });
                  }}
                />
              </Tooltip>
            </div>
            <div className="text-sm text-neutral-500 italic">
              By enabling experimental features, you agree that we may contact
              you for product feedback or usability sessions.
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        title="Delete Environment"
        isOpen={modalDeleteOpen}
        onClose={() => {
          setModalDeleteOpen(false);
          setSelectedDeleteId('');
        }}
        actionButton={
          <Button onClick={deleteApiKey} disabled={loading}>
            Delete Environment
          </Button>
        }
        className="w-96"
      >
        <div className="text-center">
          Are you sure you want to delete this environment?
        </div>
      </Modal>
    </div>
  );
};
